import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { TEST_CHILDREN, TEST_PARENT } from '../../utils/testAccounts';
import { WARMUP_DURATION_MINS, MAIN_DURATION_MINS, getTodaySchedule } from '../../utils/sessionSchedule';
import {
  CHESS_LESSONS, CHESS_TOTAL_LESSONS, chessDone, getAchievements, getRecentSessions, formatMinutes,
} from '../../utils/progress';
import { SUBSCRIPTION_LABELS, OFFER_LINE, planForChildren, formatPrice } from '../../config/siteConfig';
import DashShell from '../../components/layout/DashShell';
import WeekTimetable from '../../components/layout/WeekTimetable';
import './ParentDashboard.css';

// Parent dashboard — docs/redesign/reference-dashboards.html. Uses the same
// data as before: test children or the parent's Firestore `children` docs,
// plus the session records kept on this device.

export default function ParentDashboard() {
  const navigate = useNavigate();
  const { currentUser, userProfile, getChildren, logout } = useAuth();
  const [children, setChildren] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const testParent = !!sessionStorage.getItem('testParent');

  const loadChildren = useCallback(async () => {
    if (testParent) { setChildren(TEST_CHILDREN); setLoading(false); return; }
    if (!currentUser) return navigate('/login');
    const kids = await getChildren(currentUser.uid);
    setChildren(kids);
    setLoading(false);
    if (kids.length === 0) navigate('/parent/setup');
  }, [testParent, currentUser, getChildren, navigate]);

  useEffect(() => { loadChildren(); }, [loadChildren]);
  useEffect(() => { document.title = 'Family Overview · Talent School Africa'; }, []);

  async function handleLogout() {
    sessionStorage.removeItem('testParent');
    sessionStorage.removeItem('testChildren');
    sessionStorage.removeItem('parentPreview');
    try { await logout(); } catch (e) {}
    navigate('/');
  }

  function handleChildView(child) {
    sessionStorage.setItem('parentPreview', JSON.stringify({
      id: child.id, name: child.name, age: child.age, avatar: child.avatar,
      avatarColor: child.avatarColor, studentId: child.studentId,
      learningPrefs: child.learningPrefs, progress: child.progress,
      streak: child.streak, totalXP: child.totalXP,
      lessonsComplete: child.lessonsComplete,
      isTestAccount: child.isTestAccount,
    }));
    navigate('/child/today');
  }

  if (loading) return (
    <div className="tsa-brand pd-loading"><div className="pd-spin" /></div>
  );
  if (children.length === 0) return null;

  const child = children[activeIdx] || children[0];
  const parentName = (testParent ? TEST_PARENT.displayName : userProfile?.displayName) || 'Parent';
  const plan = (testParent ? TEST_PARENT.subscription : userProfile?.subscription) || 'trial';
  const email = testParent ? TEST_PARENT.email : (userProfile?.email || currentUser?.email);

  const done = chessDone(child);
  const achievements = getAchievements(child);
  const earned = achievements.filter(a => a.earned);
  const week = getRecentSessions(child.id, 7);
  const weekMins = week.reduce((s, d) => s + d.minutes, 0);
  const max = Math.max(...week.map(d => d.minutes), 1);
  const current = done < CHESS_LESSONS.length ? CHESS_LESSONS[done] : null;
  const today = getTodaySchedule();
  const fitPlan = planForChildren(children.length);

  // Recent activity: session records on this device (newest first), then
  // the lessons the child has completed.
  const events = [];
  [...getRecentSessions(child.id, 14)].reverse().forEach(d => {
    if (!d.state) return;
    const when = d.date === week[6].date ? 'Today' : d.date === week[5].date ? 'Yesterday'
      : new Date(d.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    events.push(d.state.completed
      ? { ic: '✅', tint: 'var(--t-green)', title: 'Finished the day\'s session', sub: `Warm-up and lesson · ${d.minutes} min`, when }
      : { ic: '▶️', tint: 'var(--yellow-bg)', title: 'Started a session', sub: d.state.warmupComplete ? 'Warm-up done, lesson in progress' : 'Warm-up in progress', when });
  });
  for (let i = Math.min(done, CHESS_LESSONS.length) - 1; i >= 0; i--) {
    const l = CHESS_LESSONS[i];
    events.push({ ic: '🏁', tint: 'var(--t-blue)', title: `Completed Lesson ${l.n}: ${l.name}`, sub: `Chess · badge “${achievements[i].name}”`, when: '' });
  }

  const nav = [
    { icon: '🏠', label: 'Overview', active: true, onClick: () => window.scrollTo(0, 0) },
    { icon: '👁️', label: `Preview ${child.name}'s view`, onClick: () => handleChildView(child) },
    { icon: '➕', label: 'Add a child', onClick: () => navigate('/parent/setup') },
    { icon: '🚪', label: 'Log out', onClick: handleLogout },
  ];

  return (
    <DashShell
      user={{ initial: parentName[0], name: parentName.split(' ')[0], color: 'var(--navy)', sub: `Parent · ${children.length} ${children.length === 1 ? 'child' : 'children'}` }}
      navLabel="Family"
      nav={nav}
      foot={<><b>Student login</b>{child.name} logs in at the student login with ID <b className="pd-id">{child.studentId}</b> and their PIN.</>}
      title="Family Overview"
      chips={<span className="chip">{SUBSCRIPTION_LABELS[plan] || plan} plan</span>}
    >
      <div className="kids" role="group" aria-label="Choose child">
        {children.map((c, i) => (
          <button key={c.id} type="button" className={`kid ${i === activeIdx ? 'on' : ''}`} aria-pressed={i === activeIdx} onClick={() => setActiveIdx(i)}>
            <span className="dash-av" style={{ background: c.avatarColor || 'var(--green)' }}>{(c.name || '?')[0]}</span>
            {c.name}{c.age ? `, ${c.age}` : ''}
          </button>
        ))}
        <button type="button" className="kid add" onClick={() => navigate('/parent/setup')}>+ Add child</button>
      </div>

      <div className="stats">
        <div className="stat" style={{ background: 'var(--t-green)' }}><div className="stat-ic">📚</div><div><b>{done} / {CHESS_TOTAL_LESSONS}</b><span>Lessons completed</span></div></div>
        <div className="stat" style={{ background: 'var(--t-blue)' }}><div className="stat-ic">⏱️</div><div><b>{formatMinutes(weekMins)}</b><span>Learning this week</span></div></div>
        <div className="stat" style={{ background: 'var(--t-peach)' }}><div className="stat-ic">🔥</div><div><b>{child.streak || 0}</b><span>Day streak</span></div></div>
        <div className="stat" style={{ background: 'var(--t-lav)' }}><div className="stat-ic">🏅</div><div><b>{earned.length}</b><span>Badges earned</span></div></div>
      </div>

      <div className="two">
        <div className="pd-col">
          <div className="card">
            <div className="card-h"><h2>{child.name}'s learning this week</h2><span className="muted">Minutes per day · finished sessions on this device</span></div>
            <div className="chart" role="img" aria-label={`Minutes learned per day, last 7 days: ${week.map(d => `${d.day} ${d.minutes}`).join(', ')}`}>
              {week.map(d => (
                <div key={d.date} className="bar" tabIndex={0} aria-label={`${d.day}: ${d.minutes} minutes`}>
                  <span className="v">{d.minutes}</span>
                  <i style={{ height: `${(d.minutes / max) * 80}%` }} />
                  <span className="tip">{d.day} · {d.minutes} min</span>
                </div>
              ))}
            </div>
            <div className="days" aria-hidden="true">{week.map(d => <span key={d.date}>{d.day}</span>)}</div>
          </div>
          <div className="card">
            <div className="card-h"><h2>Recent activity</h2>{current && <span className="muted">Next: Lesson {current.n} · {current.name}</span>}</div>
            {events.length === 0
              ? <p className="empty">No activity yet. When {child.name} starts a lesson it will show here.</p>
              : <div className="feed">{events.slice(0, 4).map((e, i) => (
                  <div key={i} className="ev">
                    <div className="ei" style={{ background: e.tint }}>{e.ic}</div>
                    <div><b>{e.title}</b><span>{e.sub}</span></div>
                    {e.when && <time>{e.when}</time>}
                  </div>
                ))}</div>}
          </div>
        </div>

        <div className="pd-col">
          <div className="card sub">
            <span className="tagp">{plan === 'trial' ? 'FREE MONTH' : 'ACTIVE'}</span>
            <h3>{SUBSCRIPTION_LABELS[plan] || plan}</h3>
            <div className="sub-line">{plan === 'trial'
              ? <>Then <b>{fitPlan.name}</b> at {formatPrice(fitPlan.monthly)} / month · {fitPlan.children}</>
              : <>{formatPrice(fitPlan.monthly)} / month · {fitPlan.children}</>}</div>
            <dl>
              <dt>Children</dt><dd>{children.length}</dd>
              <dt>Account</dt><dd>{email}</dd>
            </dl>
            <div className="sub-offer">{OFFER_LINE}</div>
          </div>
          <div className="card">
            <div className="card-h"><h2>📅 Weekly timetable</h2><span className="muted">Today: {today.label}</span></div>
            <WeekTimetable today={today.dayNum} />
          </div>
          <div className="card">
            <div className="card-h"><h2>🛡️ Safety &amp; controls</h2></div>
            <div className="ctrl">
              <div className="ctrl-row"><div>Daily session limit<span>{WARMUP_DURATION_MINS}-min warm-up + {MAIN_DURATION_MINS}-min lesson</span></div><span className="on-pill">On</span></div>
              <div className="ctrl-row"><div>Parent area locked<span>Student logins can't open these pages</span></div><span className="on-pill">On</span></div>
            </div>
          </div>
        </div>
      </div>
    </DashShell>
  );
}
